import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HD_LOCKS_KEYS_CHART_OVERLAY_MODES, HdLocksKeysChartOverlayMode } from './hd-locks-keys-metadata';
import { HdLocksKeysStateService } from './hd-locks-keys-state.service';

/** i18n-sleutels per stand: kort label op de knop, volledige uitleg als tooltip. */
const OPTION_KEYS: Readonly<Record<HdLocksKeysChartOverlayMode, { label: string; tooltip: string }>> = {
  off: { label: 'summary.overlayOff', tooltip: 'summary.overlayOffTooltip' },
  all: { label: 'summary.overlayAll', tooltip: 'summary.overlayAllTooltip' },
  chart: { label: 'summary.overlayChart', tooltip: 'summary.overlayChartTooltip' },
};

/**
 * "Toon op de chart" als drie-standen-knop (spec 2026-10-10): uit · alle locks
 * & keys van de periode · enkel chartactivaties, alle drie zichtbaar naast
 * elkaar (een cyclend vinkje bleek verwarrend, Steven 2026-10-10). Schrijft
 * {@link HdLocksKeysStateService.chartOverlayMode} (bewaard in de settings);
 * gedeeld door de basisinfo in het Geavanceerd-paneel en het dedicated paneel.
 */
@Component({
  selector: 'hd-locks-keys-overlay-toggle',
  imports: [ESegmentedToggleComponent],
  templateUrl: './hd-locks-keys-overlay-toggle.component.html',
  styleUrl: './hd-locks-keys-overlay-toggle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysOverlayToggleComponent {
  private readonly state = inject(HdLocksKeysStateService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected t(key: string): string {
    return this.gateInfo.locksKeysTextIn(key, this.lang());
  }

  protected readonly mode = this.state.chartOverlayMode;

  /** Knoppen uit · alle · chart (gedeelde e-segmented-toggle, in L&K-blauw). */
  protected readonly options = computed<readonly ESegmentedToggleOption<HdLocksKeysChartOverlayMode>[]>(() =>
    HD_LOCKS_KEYS_CHART_OVERLAY_MODES.map(mode => ({
      value: mode,
      label: this.t(OPTION_KEYS[mode].label),
      title: this.t(OPTION_KEYS[mode].tooltip),
    })),
  );

  protected select(mode: HdLocksKeysChartOverlayMode): void {
    this.state.chartOverlayMode.set(mode);
  }
}
